import { inheritLegacyBrandButtons } from '../utils/legacyBrandButtons';
/**
 * Tenant Context for Super Admin Panel
 *
 * Provides tenant configuration and branding throughout the superadmin application.
 * Uses accountBranding.superadmin for superadmin-specific branding.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { environment } from '../env/environment';
import logger from '../utils/logger';

const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

// Default branding (fallback)
const DEFAULT_BRANDING = {
  id: null,
  name: 'Empower Care',
  brandName: 'Empower Care',
  slug: 'default',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: '#06AB8B',
  secondaryColor: '#049572',
  features: {}
};

// Create context
const TenantContext = createContext({
  tenant: DEFAULT_BRANDING,
  loading: true,
  error: null,
  refetch: () => {},
  setTenant: () => {}
});

/**
 * Clear tenant data from localStorage and window
 */
const clearTenantData = () => {
  localStorage.removeItem('superadmin_tenant_id');
  localStorage.removeItem('superadmin_tenant_slug');
  localStorage.removeItem('superadmin_tenant_domain_slug');
  window.__TENANT_ID__ = null;
};

/**
 * Extract tenant identifier from current URL
 */
const extractTenantFromUrl = () => {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);

  // 1. Check query parameter first (?tenant=slug)
  const queryTenant = searchParams.get('tenant');
  if (queryTenant) {
    localStorage.setItem('superadmin_tenant_slug', queryTenant);
    return { type: 'slug', value: queryTenant };
  }

  // 2. Check for cached tenant slug from a previous visit (works on all environments)
  const cachedSlug = localStorage.getItem('superadmin_tenant_slug');
  if (cachedSlug) {
    logger.info('[TenantContext] Using cached tenant slug:', cachedSlug);
    return { type: 'slug', value: cachedSlug };
  }

  // 3. For localhost without cached tenant - return null for default
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    clearTenantData();
    return null;
  }

  // 4. For production: Check if it's a custom domain
  const isCustomDomain = !hostname.includes('empowercare') &&
                         !hostname.includes('kinsta') &&
                         !hostname.includes('localhost');

  if (isCustomDomain) {
    // First try domain lookup, but also extract potential tenant slug from domain
    // Pattern: superadmin.orthobiogen.com -> orthobiogen
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      // Get the main domain name (second-to-last part before TLD)
      const mainDomain = parts[parts.length - 2];
      logger.info('[TenantContext] Custom domain detected:', hostname, '| Extracted slug:', mainDomain);
      // Store extracted slug as fallback
      localStorage.setItem('superadmin_tenant_domain_slug', mainDomain);
    }
    return { type: 'domain', value: hostname };
  }

  // 5. For production: Extract subdomain
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const subdomain = parts[0];
    // Handle superadmin subdomain pattern: elitepain-superadmin -> elitepain
    if (subdomain.endsWith('-superadmin')) {
      return { type: 'slug', value: subdomain.replace('-superadmin', '') };
    }
    if (!['www', 'api', 'admin', 'app', 'superadmin'].includes(subdomain)) {
      return { type: 'slug', value: subdomain };
    }
  }

  return null;
};

/**
 * Fetch tenant configuration from API
 */
const fetchTenantConfig = async (tenantInfo) => {
  try {
    let url;

    if (tenantInfo.type === 'slug') {
      url = `${environment.baseUrl}/api/tenants/slug/${tenantInfo.value}`;
    } else if (tenantInfo.type === 'domain') {
      url = `${environment.baseUrl}/api/tenants/domain/${tenantInfo.value}`;
    } else {
      return null;
    }

    const response = await fetch(url, {
      headers: {
        'X-Tenant-Domain': tenantInfo.type === 'domain' ? tenantInfo.value : '',
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.warn('Tenant not found:', tenantInfo.value);
        return null;
      }
      throw new Error(`Failed to fetch tenant: ${response.status}`);
    }

    const data = await response.json();
    return data.tenant;
  } catch (error) {
    logger.error('Error fetching tenant config:', error);
    return null;
  }
};

/**
 * Transform API response to usable tenant object
 * Uses accountBranding.superadmin for superadmin-specific branding
 */
export const transformTenant = (apiTenant) => {
  if (!apiTenant) return null;

  // Get superadmin-specific branding from accountBranding (if exists)
  const superadminBranding = apiTenant.account_branding?.superadmin || {};

  logger.info('[SuperAdmin TenantContext] Raw API tenant:', apiTenant);
  logger.info('[SuperAdmin TenantContext] Raw account_branding:', apiTenant.account_branding);
  logger.info('[SuperAdmin TenantContext] SuperAdmin branding extracted:', superadminBranding);
  logger.info('[SuperAdmin TenantContext] Using primaryColor:', superadminBranding.primaryColor || apiTenant.primary_color || DEFAULT_BRANDING.primaryColor);

  return {
    id: apiTenant.id,
    name: apiTenant.name,
    brandName: decodeHtmlEntities(apiTenant.brand_name || apiTenant.name),
    slug: apiTenant.slug,
    logoUrl: apiTenant.logo_url,
    signinLogoUrl: apiTenant.signin_logo_url,
    faviconUrl: apiTenant.favicon_url,
    // Use superadmin-specific colors if available, otherwise fallback to global
    primaryColor: superadminBranding.primaryColor || apiTenant.primary_color || DEFAULT_BRANDING.primaryColor,
    secondaryColor: superadminBranding.secondaryColor || apiTenant.secondary_color || DEFAULT_BRANDING.secondaryColor,
    buttonHoverColor: superadminBranding.buttonHoverColor || apiTenant.button_hover_color || null,
    buttonBoxShadow: superadminBranding.buttonBoxShadow || apiTenant.button_box_shadow || null,
    buttonStyles: inheritLegacyBrandButtons(superadminBranding.buttonStyles, 'superadmin', superadminBranding.primaryColor || apiTenant.primary_color || DEFAULT_BRANDING.primaryColor), // SuperAdmin-specific button styles
    customDomain: apiTenant.custom_domain,
    features: apiTenant.features || {},
    settings: apiTenant.settings || {}
  };
};

/**
 * Apply tenant branding to document
 */
export const applyBrandingToDocument = (tenant) => {
  // Set CSS variables for colors
  document.documentElement.style.setProperty('--color-primary', tenant.primaryColor);
  document.documentElement.style.setProperty('--color-secondary', tenant.secondaryColor);
  document.documentElement.style.setProperty('--color-primary-light', tenant.primaryColor + '20');

  // Button styling - use primary/secondary colors
  const hoverColor = tenant.buttonHoverColor || tenant.secondaryColor;
  document.documentElement.style.setProperty('--color-primary-hover', hoverColor);
  document.documentElement.style.setProperty('--color-primary-dark', tenant.secondaryColor);
  document.documentElement.style.setProperty('--color-button-hover', hoverColor);
  document.documentElement.style.setProperty('--button-box-shadow', tenant.buttonBoxShadow || 'none');

  // Get button styles from tenant (from accountBranding.superadmin.buttonStyles)
  const buttonStyles = tenant.buttonStyles || {};

  logger.info('[SuperAdmin TenantContext] Applying colors - primary:', tenant.primaryColor, 'secondary:', tenant.secondaryColor);
  logger.info('[SuperAdmin TenantContext] Applying button styles:', buttonStyles);

  // Sign-in button - use buttonStyles if available, else tenant colors
  document.documentElement.style.setProperty('--btn-signin-bg', buttonStyles.signin?.bg || tenant.primaryColor);
  document.documentElement.style.setProperty('--btn-signin-text', buttonStyles.signin?.text || '#ffffff');
  document.documentElement.style.setProperty('--btn-signin-hover', buttonStyles.signin?.hover || tenant.secondaryColor);

  // Create New Admin button
  document.documentElement.style.setProperty('--btn-create-admin-bg', buttonStyles.createAdmin?.bg || tenant.primaryColor);
  document.documentElement.style.setProperty('--btn-create-admin-text', buttonStyles.createAdmin?.text || '#ffffff');
  document.documentElement.style.setProperty('--btn-create-admin-hover', buttonStyles.createAdmin?.hover || tenant.secondaryColor);

  // Edit Admin button
  document.documentElement.style.setProperty('--btn-edit-admin-bg', buttonStyles.editAdmin?.bg || '#2196F3');
  document.documentElement.style.setProperty('--btn-edit-admin-text', buttonStyles.editAdmin?.text || '#ffffff');
  document.documentElement.style.setProperty('--btn-edit-admin-hover', buttonStyles.editAdmin?.hover || '#1976D2');

  // Delete Admin button
  document.documentElement.style.setProperty('--btn-delete-admin-bg', buttonStyles.deleteAdmin?.bg || '#f44336');
  document.documentElement.style.setProperty('--btn-delete-admin-text', buttonStyles.deleteAdmin?.text || '#ffffff');
  document.documentElement.style.setProperty('--btn-delete-admin-hover', buttonStyles.deleteAdmin?.hover || '#d32f2f');

  // Set tenant ID on window for API access
  if (tenant.id) {
    window.__TENANT_ID__ = tenant.id;
    localStorage.setItem('superadmin_tenant_id', tenant.id);
    localStorage.setItem('superadmin_tenant_slug', tenant.slug);
  } else {
    clearTenantData();
  }

  // Remove ALL existing favicon links first
  const existingIcons = document.querySelectorAll("link[rel*='icon']");
  existingIcons.forEach(icon => icon.remove());

  // Set favicon with cache-busting
  if (tenant.faviconUrl) {
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/x-icon';
    faviconLink.href = tenant.faviconUrl + '?_t=' + Date.now();
    document.head.appendChild(faviconLink);
  }

  // Set page title
  if (tenant.brandName) {
    document.title = `${tenant.brandName} - Super Admin`;
  } else {
    document.title = 'Empower Care - Super Admin';
  }
};

/**
 * Tenant Provider Component
 */
export const TenantProvider = ({ children }) => {
  const [tenant, setTenantState] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTenantSlug, setCurrentTenantSlug] = useState(null);

  const loadTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if tenant was pre-fetched by the early detection script in index.html
      if (window.__TENANT_DATA__) {
        logger.info('[SuperAdmin TenantContext] Using pre-fetched tenant data:', window.__TENANT_DATA__.id);
        const transformedTenant = transformTenant(window.__TENANT_DATA__);

        if (transformedTenant.id) {
          window.__TENANT_ID__ = transformedTenant.id;
          localStorage.setItem('superadmin_tenant_id', String(transformedTenant.id));
          localStorage.setItem('superadmin_tenant_slug', transformedTenant.slug);
        }

        setTenantState(transformedTenant);
        applyBrandingToDocument(transformedTenant);
        setCurrentTenantSlug(transformedTenant.slug);
        delete window.__TENANT_DATA__;
        return;
      }

      const tenantInfo = extractTenantFromUrl();

      if (!tenantInfo) {
        setTenantState(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
        setCurrentTenantSlug(null);
        return;
      }

      if (tenantInfo.value === currentTenantSlug) {
        setLoading(false);
        return;
      }

      let apiTenant = await fetchTenantConfig(tenantInfo);

      // If domain lookup failed, try fallback to extracted slug from custom domain
      // e.g., superadmin.orthobiogen.com -> try slug 'orthobiogen'
      if (!apiTenant && tenantInfo.type === 'domain') {
        const fallbackSlug = localStorage.getItem('superadmin_tenant_domain_slug');
        if (fallbackSlug) {
          logger.info('[SuperAdmin TenantContext] Domain lookup failed, trying slug fallback:', fallbackSlug);
          apiTenant = await fetchTenantConfig({ type: 'slug', value: fallbackSlug });
        }
      }

      if (apiTenant) {
        const transformedTenant = transformTenant(apiTenant);
        setTenantState(transformedTenant);
        applyBrandingToDocument(transformedTenant);
        setCurrentTenantSlug(transformedTenant.slug);
        // Store the slug for future reference
        localStorage.setItem('superadmin_tenant_slug', transformedTenant.slug);
      } else {
        clearTenantData();
        localStorage.removeItem('superadmin_tenant_domain_slug');
        setTenantState(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
        setCurrentTenantSlug(null);
      }
    } catch (err) {
      logger.error('Error loading tenant:', err);
      setError(err.message);
      setTenantState(DEFAULT_BRANDING);
      applyBrandingToDocument(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [currentTenantSlug]);

  const setTenant = useCallback(async (tenantSlug) => {
    try {
      setLoading(true);
      const apiTenant = await fetchTenantConfig({ type: 'slug', value: tenantSlug });

      if (apiTenant) {
        const transformedTenant = transformTenant(apiTenant);
        setTenantState(transformedTenant);
        applyBrandingToDocument(transformedTenant);
        localStorage.setItem('superadmin_tenant_slug', tenantSlug);
        setCurrentTenantSlug(tenantSlug);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenant();
  }, []);

  useEffect(() => {
    const handleUrlChange = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const urlTenant = searchParams.get('tenant');

      if (urlTenant && urlTenant !== currentTenantSlug) {
        loadTenant();
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [currentTenantSlug, loadTenant]);

  const value = {
    tenant,
    loading,
    error,
    refetch: loadTenant,
    setTenant,
    getLogo: () => tenant.logoUrl,
    getSigninLogo: () => tenant.signinLogoUrl || tenant.logoUrl,
    getPrimaryColor: () => tenant.primaryColor,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
